import type { Integration, Event as SentryEvent } from "@sentry/core";

import { nanoid } from "nanoid";
import { pick } from "radash";
import { mergeDeepRight, pipe } from "ramda";

import { getClientSideArgusProjection } from "@solvari/argus";
import { getArgusEvents } from "@solvari/argus/thin-client";
import { deepToString, getEnv, memoize } from "@solvari/utils";

import type {
  AddDataToProject,
  SentryProjectMetadata,
  SentryRootProject,
} from "@/lib/integrations/sentry/types.ts";

import { sentryProjects } from "@/lib/integrations/sentry/projects.ts";

const applyDataToEvent =
  (addData?: AddDataToProject) =>
  (event: Readonly<SentryEvent>): SentryEvent => {
    if (!addData) {
      return event;
    }

    return mergeDeepRight(event, deepToString(addData()));
  };

const addGlobalData: AddDataToProject = () => ({
  tags: {
    envBE: getEnv().config.envBE,
    localeCode: getEnv().config.localeCode,
  },
  contexts: {
    dataLayer: { events: window.dataLayer ?? [] },
    argusEvents: { events: getArgusEvents() },
    metadata: { ...getClientSideArgusProjection() },
  },
});

const solvariContextIntegration = (
  rootProject: SentryRootProject,
): Integration => ({
  name: "SolvariContext",
  processEvent: pipe(
    applyDataToEvent(addGlobalData),
    applyDataToEvent(rootProject.addData),
  ),
});

const getEventModuleMeta = memoize(
  (event: SentryEvent) => {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;

    if (!frames) {
      return;
    }

    // Find the last frame with module metadata containing a project
    /* oxlint-disable typescript/no-unsafe-member-access */
    return [...frames]
      .toReversed()
      .find((frame) => frame.module_metadata?.project)?.module_metadata as
      | SentryProjectMetadata
      | undefined;
  },
  { key: (event) => event.event_id ?? nanoid(10), staleTime: 5000 },
);

const getEventProject = (event: SentryEvent) => {
  const moduleMeta = getEventModuleMeta(event);
  if (!moduleMeta?.project || !(moduleMeta.project in sentryProjects)) {
    return;
  }

  return moduleMeta as Required<SentryProjectMetadata>;
};

const solvariModuleMetaIntegration = (): Integration => ({
  name: "SolvariBuildType",
  processEvent(event) {
    const solvariProject = getEventModuleMeta(event);

    if (!solvariProject) {
      return event;
    }
    return applyDataToEvent(() => pick(solvariProject, ["tags", "contexts"]))(
      event,
    );
  },
});

const solvariMicroFeContextIntegration = (): Integration => ({
  name: "SolvariMicroFe",
  processEvent(event) {
    const solvariProject = getEventProject(event);

    if (!solvariProject) {
      return event;
    }

    return pipe(
      applyDataToEvent(() => ({
        tags: {
          project: event.tags?.project ?? solvariProject.project,
          package: solvariProject.project,
        },
      })),
      applyDataToEvent(globalThis.sentryProjects?.[solvariProject.project]),
    )(event);
  },
});

const solvariFilterIntegration = (): Integration => ({
  name: "SolvariFilter",
  processEvent(event) {
    if (
      event.exception?.values?.[0]?.value === "undefined" ||
      event.contexts?.browser?.name === "HeadlessChrome"
    ) {
      return null;
    }
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (frames?.length === 1 && frames[0]?.filename === "<anonymous>") {
      return null;
    }
    return event;
  },
});

export {
  getEventProject,
  solvariContextIntegration,
  solvariFilterIntegration,
  solvariMicroFeContextIntegration,
  solvariModuleMetaIntegration,
};
